import React, { useState, useEffect } from 'react';
import {collection,addDoc} from 'firebase/firestore';
import {publicCollection} from '../lib/publicContent';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { MessageSquare, Send, Trash2, User, Clock } from 'lucide-react';
import { Button } from './ui/Button';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';

interface Comment {
  id: string;
  postId: string;
  postType: 'news' | 'service' | 'page';
  authorName: string;
  authorEmail?: string;
  content: string;
  createdAt: any;
  status: 'pending' | 'approved' | 'rejected';
  parentId?: string;
  replies?: Comment[];
}

interface CommentSectionProps {
  postId: string;
  postType: 'news' | 'service' | 'page';
  enabled: boolean;
}

export const CommentSection: React.FC<CommentSectionProps> = ({ postId, postType, enabled }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!enabled) return;

    let cancelled=false;
    publicCollection('comments').then(rows=> {
      if(!cancelled) setComments(rows.filter(c=>c.postId===postId&&c.postType===postType&&!c.parentId).sort((a,b)=>Date.parse(b.createdAt)-Date.parse(a.createdAt)));
    }).catch(error=>console.error('Public comments:',error));
    return ()=> {cancelled=true;};
  }, [postId, postType, enabled]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !authorName.trim()) return;

    setLoading(true);
    try {
      await addDoc(collection(db, 'comments'), {
        postId,
        postType,
        authorName: authorName.trim(),
        authorEmail: authorEmail.trim() || null,
        content: newComment.trim(),
        createdAt: new Date(),
        status: 'pending',
        parentId: null,
      });

      setNewComment('');
      setShowForm(false);
      alert('Bình luận đã được gửi và đang chờ phê duyệt!');
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Có lỗi khi gửi bình luận. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  if (!enabled) return null;

  return (
    <div className="mt-12 pt-8 border-t border-gray-200">
      <div className="flex items-center gap-2 mb-6">
        <MessageSquare className="w-5 h-5 text-primary" />
        <h3 className="text-xl font-bold text-gray-900">
          Bình luận ({comments.length})
        </h3>
      </div>

      {/* Comment List */}
      <div className="space-y-4 mb-8">
        {comments.length === 0 ? (
          <p className="text-gray-500 text-center py-8 bg-gray-50 rounded-lg">
            Chưa có bình luận nào. Hãy là người đầu tiên bình luận!
          </p>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900">{comment.authorName}</span>
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {comment.createdAt && 
                        formatDistanceToNow(comment.createdAt?.toDate ? comment.createdAt.toDate() : new Date(comment.createdAt), { addSuffix: true, locale: vi })
                      }
                    </span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{comment.content}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Comment Form */}
      {!showForm ? (
        <Button
          variant="outline"
          onClick={() => setShowForm(true)}
          className="w-full md:w-auto"
          icon={<MessageSquare className="w-4 h-4" />}
        >
          Viết bình luận
        </Button>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-4">Viết bình luận</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Họ tên <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Nhập họ tên"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email (không bắt buộc)
              </label>
              <input
                type="email"
                value={authorEmail}
                onChange={(e) => setAuthorEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="email@example.com"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nội dung <span className="text-red-500">*</span>
            </label>
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              rows={4}
              placeholder="Chia sẻ ý kiến của bạn..."
              required
            />
          </div>

          <div className="flex gap-3">
            <Button
              type="submit"
              variant="primary"
              loading={loading}
              icon={<Send className="w-4 h-4" />}
            >
              Gửi bình luận
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowForm(false)}
            >
              Hủy
            </Button>
          </div>

          <p className="text-xs text-gray-500 mt-4">
            * Bình luận sẽ được kiểm duyệt trước khi hiển thị
          </p>
        </form>
      )}
    </div>
  );
};

export default CommentSection;
